import {
  ApiOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  ExportOutlined,
  EyeOutlined,
  LinkOutlined,
  LockOutlined,
  LogoutOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { DOCS_URLS } from "@p0security/frontend-core";
import { AppPaths } from "@p0security/shared/routes/constants";
import { ItemType, Spacing, message } from "@p0security/ui";
import { LinkWithEnvironment } from "components/Assessment/components/LinkWithEnvironment";
import { Link, NavLink } from "react-router-dom";
import { spacing } from "styles/spacing";

import { getNavLinkClassName } from "../../../utils/activeNavLinkClassName";
import { UserRole } from "../../Login";
import {
  AGENTIC_ROLES,
  CONFIG_ROUTES_ROLES,
  ENVIRONMENT_ROLES,
  ENV_SETTINGS_ROLES,
  JIT_POLICY_ROLES,
} from "../roles";

const newSpacer = (key: string): ItemType => ({
  key,
  type: "divider",
  style: { borderColor: "transparent", marginTop: `${spacing.scale.md}px` },
});

export const newSidebarItems = (
  userAuthz: Set<UserRole>,
  hasEnvironments?: boolean,
  hasAgenticGateway?: boolean
): ItemType[] => {
  const items: ItemType[] = [];

  if (ENVIRONMENT_ROLES.some((r) => userAuthz.has(r))) {
    items.push(
      {
        key: AppPaths.Dashboard,
        icon: <DashboardOutlined />,
        label: (
          <LinkWithEnvironment to={AppPaths.Dashboard}>
            Dashboard
          </LinkWithEnvironment>
        ),
      },
      {
        key: AppPaths.Inventory,
        icon: <DatabaseOutlined />,
        label: (
          <LinkWithEnvironment to={AppPaths.Inventory}>
            Inventory
          </LinkWithEnvironment>
        ),
      },
      {
        key: AppPaths.Posture,
        icon: <EyeOutlined />,
        label: (
          <LinkWithEnvironment to={AppPaths.Posture}>
            Posture
          </LinkWithEnvironment>
        ),
      }
    );
  }

  if (hasAgenticGateway && AGENTIC_ROLES.some((role) => userAuthz.has(role))) {
    items.push({
      key: AppPaths.AgenticAccess,
      icon: <RobotOutlined />,
      label: (
        <NavLink to={AppPaths.AgenticAccess} className={getNavLinkClassName}>
          Agentic Access
        </NavLink>
      ),
    });
  }

  items.push({
    key: AppPaths.AccessManagement,
    icon: <ClockCircleOutlined />,
    label: (
      <NavLink to={AppPaths.AccessManagement} className={getNavLinkClassName}>
        Access Management
      </NavLink>
    ),
  });

  if (JIT_POLICY_ROLES.some((r) => userAuthz.has(r))) {
    items.push({
      key: AppPaths.Policies,
      icon: <SafetyCertificateOutlined />,
      label: (
        <NavLink to={AppPaths.Policies} className={getNavLinkClassName}>
          Policy Studio
        </NavLink>
      ),
    });
  }

  if (hasEnvironments && ENV_SETTINGS_ROLES.some((r) => userAuthz.has(r))) {
    items.push({
      key: AppPaths.Settings,
      icon: <SettingOutlined />,
      label: (
        <LinkWithEnvironment to={AppPaths.Settings}>
          Settings
        </LinkWithEnvironment>
      ),
    });
  }

  if (CONFIG_ROUTES_ROLES.some((r) => userAuthz.has(r))) {
    items.push(newSpacer("spacer-admin"), {
      key: AppPaths.Integrations,
      icon: <ApiOutlined />,
      label: (
        <NavLink to={AppPaths.Integrations} className={getNavLinkClassName}>
          Integrations
        </NavLink>
      ),
    });
  }

  if (userAuthz.has("owner")) {
    items.push({
      key: AppPaths.P0Admin,
      icon: <LockOutlined />,
      label: (
        <NavLink to={AppPaths.P0Admin} className={getNavLinkClassName}>
          P0 Management
        </NavLink>
      ),
    });
  }

  items.push(newSpacer("spacer-docs"));

  items.push({
    key: "docs",
    icon: <LinkOutlined />,
    label: (
      <Link to={DOCS_URLS.BASE} target="_blank" rel="noreferrer">
        Documentation{" "}
        <Spacing asChild ml={4}>
          <ExportOutlined />
        </Spacing>
      </Link>
    ),
  });

  return items;
};

export const newBottomItems = (signOut: () => Promise<void>): ItemType[] => [
  {
    key: "logout",
    icon: <LogoutOutlined />,
    label: "Logout",
    title: "Logout",
    danger: true,
    onClick: async () => {
      try {
        await signOut();
      } catch {
        message.error("Failed to sign out. Please try again.");
      }
    },
  },
];
